import React, { useState } from 'react';
import { ShieldAlert, CheckCircle, Calculator, TrendingUp } from 'lucide-react';

export default function RiskGuardian({ positions, totalPnL, virtualWallet, disciplineScore }) {
  const [capital, setCapital] = useState(virtualWallet || 1000000);
  const [riskPercent, setRiskPercent] = useState(1);
  const [entryPrice, setEntryPrice] = useState(24400);
  const [stopLoss, setStopLoss] = useState(24320);
  const [targetPrice, setTargetPrice] = useState(24600);
  const [calcResult, setCalcResult] = useState(null);

  const calculateRisk = (e) => {
    e.preventDefault();
    const cap = parseFloat(capital);
    const riskPct = parseFloat(riskPercent);
    const entry = parseFloat(entryPrice);
    const sl = parseFloat(stopLoss);
    const tgt = parseFloat(targetPrice);

    if (!cap || !riskPct || !entry || !sl || entry === sl) {
      alert("Please enter valid parameters. Entry and Stop-Loss cannot be equal.");
      return;
    }

    const maxRiskAmount = (cap * riskPct) / 100;
    const riskPerUnit = Math.abs(entry - sl);
    const rewardPerUnit = Math.abs(tgt - entry);
    
    // For Nifty lot size = 50, calculate units/lots
    const rawUnits = maxRiskAmount / riskPerUnit;
    const lots = Math.floor(rawUnits / 50) > 0 ? Math.floor(rawUnits / 50) : 1;
    const recommendedQty = lots * 50;
    const actualRisk = recommendedQty * riskPerUnit;
    const potentialReward = recommendedQty * rewardPerUnit;
    const riskRewardRatio = (potentialReward / actualRisk).toFixed(2);

    setCalcResult({
      maxRiskAmount: maxRiskAmount.toFixed(2),
      riskPerUnit: riskPerUnit.toFixed(2),
      recommendedQty: recommendedQty,
      lots: lots,
      actualRisk: actualRisk.toFixed(2),
      potentialReward: potentialReward.toFixed(2),
      riskRewardRatio: riskRewardRatio,
      verdict: actualRisk <= maxRiskAmount ? "✅ Position sizing complies with institutional risk management rules." : "⚠️ Warning: Risk exceeds your defined risk threshold."
    });
  };

  return (
    <div style={{ padding: '30px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', minHeight: '100vh', fontFamily: "'Plus Jakarta Sans', sans-serif", boxSizing: 'border-box', overflowY: 'auto' }}>
      
      {/* Header */}
      <div style={{ marginBottom: '30px' }}>
        <h2 style={{ fontFamily: "'Cinzel', serif", fontSize: '26px', color: 'var(--vyodha-gold)', margin: 0, display: 'flex', alignItems: 'center', gap: '10px' }}>
          <ShieldAlert size={28} /> AI Risk Guardian & Position Sizing Desk
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: '13px', margin: '5px 0 0 0' }}>Never risk more than your predefined capital limit per trade.</p>
      </div>

      {/* Main Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))', gap: '25px' }}>
        
        {/* Left: Input Form */}
        <div style={{ backgroundColor: 'var(--bg-panel)', padding: '25px', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
          <h3 style={{ color: 'var(--vyodha-gold)', fontSize: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <Calculator size={20} /> Trade Risk Parameters
          </h3>

          <form onSubmit={calculateRisk} style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Total Capital (₹ / $)</label>
              <input 
                type="number" 
                value={capital} 
                onChange={(e) => setCapital(e.target.value)} 
                style={{ width: '100%', padding: '12px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} 
              />
            </div>

            <div>
              <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Max Risk per Trade (%)</label>
              <input 
                type="number" 
                step="0.1"
                value={riskPercent} 
                onChange={(e) => setRiskPercent(e.target.value)} 
                style={{ width: '100%', padding: '12px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} 
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
              <div>
                <label style={{ fontSize: '12px', color: 'var(--text-muted)', display: 'block', marginBottom: '5px' }}>Entry Price</label>
                <input 
                  type="number" 
                  value={entryPrice} 
                  onChange={(e) => setEntryPrice(e.target.value)} 
                  style={{ width: '100%', padding: '12px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid var(--border-color)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} 
                />
              </div>
              <div>
                <label style={{ fontSize: '12px', color: '#EF4444', display: 'block', marginBottom: '5px' }}>Stop Loss (SL)</label>
                <input 
                  type="number" 
                  value={stopLoss} 
                  onChange={(e) => setStopLoss(e.target.value)} 
                  style={{ width: '100%', padding: '12px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid rgba(239, 68, 68, 0.4)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} 
                />
              </div>
            </div>

            <div>
              <label style={{ fontSize: '12px', color: '#10B981', display: 'block', marginBottom: '5px' }}>Target Price</label>
              <input 
                type="number" 
                value={targetPrice} 
                onChange={(e) => setTargetPrice(e.target.value)} 
                style={{ width: '100%', padding: '12px', backgroundColor: 'var(--bg-main)', color: 'var(--text-main)', border: '1px solid rgba(16, 185, 129, 0.4)', borderRadius: '8px', outline: 'none', boxSizing: 'border-box' }} 
              />
            </div>

            <button type="submit" style={{ backgroundColor: '#D4AF37', color: '#000', border: 'none', padding: '14px', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer', marginTop: '10px', boxShadow: '0 4px 15px rgba(212, 175, 55, 0.3)' }}>
              Calculate Safe Quantity 🛡️
            </button>
          </form>
        </div>

        {/* Right: Results & Verdict */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          
          <div style={{ backgroundColor: 'var(--bg-panel)', padding: '25px', borderRadius: '16px', border: '1px solid var(--border-color)', flex: 1 }}>
            <h3 style={{ color: 'var(--vyodha-gold)', fontSize: '18px', marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '8px' }}>
              <TrendingUp size={20} /> Execution Plan & Sizing
            </h3>

            {calcResult ? (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '15px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Recommended Quantity:</span>
                  <strong style={{ color: '#38BDF8', fontSize: '16px' }}>{calcResult.recommendedQty} Qty ({calcResult.lots} Lot)</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Max Risk Amount:</span>
                  <strong style={{ color: '#EF4444' }}>₹ {calcResult.actualRisk}</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Potential Reward:</span>
                  <strong style={{ color: '#10B981' }}>₹ {calcResult.potentialReward}</strong>
                </div>

                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border-color)' }}>
                  <span style={{ color: 'var(--text-muted)' }}>Risk-Reward Ratio:</span>
                  <strong style={{ color: '#D4AF37' }}>1 : {calcResult.riskRewardRatio}</strong>
                </div>

                <div style={{ marginTop: '10px', padding: '12px', backgroundColor: 'rgba(16, 185, 129, 0.1)', border: '1px solid #10B981', borderRadius: '8px', color: '#10B981', fontSize: '12px' }}>
                  {calcResult.verdict}
                </div>
              </div>
            ) : (
              <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--text-muted)' }}>
                <p>Enter parameters on the left and click calculate to view institutional position sizing.</p>
              </div>
            )}
          </div>

          {/* Current Portfolio Risk Summary */}
          <div style={{ backgroundColor: 'var(--bg-panel)', padding: '20px', borderRadius: '16px', border: '1px solid var(--border-color)' }}>
            <h4 style={{ color: 'var(--vyodha-gold)', fontSize: '14px', marginBottom: '10px' }}>Active Portfolio Risk Status</h4>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Discipline Score:</span>
              <strong style={{ color: disciplineScore > 75 ? '#10B981' : '#F59E0B' }}>{disciplineScore} / 100</strong>
            </div>
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px' }}>
              <span style={{ color: 'var(--text-muted)' }}>Open Positions P&L:</span>
              <strong className={totalPnL >= 0 ? 'text-green' : 'text-red'}>₹ {totalPnL.toFixed(2)}</strong>
            </div>
          </div>

        </div>

      </div>
    </div>
  );
}